import {createAsyncThunk} from '@reduxjs/toolkit';
import {handleApiError} from 'common/src/modules/errorHandling/handleApiError';
import {AvailableDimension, InsightsPageState} from '../insightsPage.types';
import {updateInsightsPage} from '../insightsPage.slice';
import campaignsApi from '../../../campaigns.api';
import campaignAnalyticsApi from 'common/src/modules/campaignAnalytics/campaignAnalytics.api';
import _map from 'lodash/map';
import getDimensionUiDescription from 'common/src/modules/analytics/utils/getDimensionUiDescription';
import {RootState} from 'common/src/redux/store';
import {BreadcrumbName} from 'common/src/modules/breadcrumbs/breadcrumbs.enums';
import {DimensionValue} from 'common/src/modules/analytics/analytics.types';

export const loadInsightsPage = createAsyncThunk<
  void,
  {
    campaignId: number;
    campaignVersionId: number;
  }
>(
  `${BreadcrumbName.CampaignInsights}/loadInsightsPage`,
  async ({campaignId, campaignVersionId}, {getState, dispatch}) => {
    try {
      const state = getState() as RootState;
      const {
        campaignPage: {
          campaignTree: {campaign},
          insightsPage: {measuredPeriod},
        },
      } = state;

      const dashboards = await campaignsApi.getCampaignDashboards(campaignId);

      const dimensions = await campaignAnalyticsApi.getOptionalDimensions(
        campaignId,
        campaignVersionId,
      );

      const filters = await campaignAnalyticsApi.getAvailableFilters({
        campaignId,
        campaignVersionId,
        isSocial: !!campaign.isSocial,
        metrics: [],
      });

      const availableFilters = _map(filters, ({value, label}) => ({
        value,
        label,
      }));

      const availableDimensions: AvailableDimension[] = [
        ..._map(dimensions, ({value, label}) => ({
          label,
          value,
          description: getDimensionUiDescription(value),
        })),
        {
          label: 'Month',
          value: DimensionValue.Month,
          description: getDimensionUiDescription(DimensionValue.Month),
        },
        {
          label: 'Week',
          value: DimensionValue.Week,
          description: getDimensionUiDescription(DimensionValue.Week),
        },
      ];

      const {availableMetricsWithTotals, activeSince} =
        await campaignAnalyticsApi.getGeneralStatistics(campaignId);

      const insightsPage: InsightsPageState = {
        measuredPeriod: {
          activeSince,
          timezone: measuredPeriod.timezone,
          timezoneOptions: measuredPeriod.timezoneOptions,
        },
        availableMetricsWithTotals,
        availableDimensions,
        dashboards,
        widgets: [],
        availableFilters,
      };

      dispatch(updateInsightsPage(insightsPage));
    } catch (error) {
      handleApiError(error);
    }
  },
);
